import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/ui/status-badge";
import { SignalProgress } from "@/components/ui/signal-progress";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Briefcase, BookOpen, Sparkles, MapPin, Clock, Loader2, MessageCircle, Check, X } from "lucide-react";
import { useProfile } from "@/hooks/useProfile";
import { useStudentContactRequests } from "@/hooks/useContactRequests";
import { useCareerSignals } from "@/hooks/useCareerSignals";
import { toast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { useState } from "react";
import { Textarea } from "@/components/ui/textarea";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { getCompanyName, getCompanyLogoUrl } from "@/lib/jobUtils";

export default function AlunoHome() {
  const navigate = useNavigate();
  const { profile, isLoading } = useProfile();
  const { requests: contactRequests, pendingCount, respond } = useStudentContactRequests();
  const { signals, userSignals, isLoading: signalsLoading } = useCareerSignals();
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");

  // Fetch real featured jobs (latest 3 published)
  const { data: featuredJobs } = useQuery({
    queryKey: ["featured_jobs_home"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("job_posts")
        .select("id, title, employment_type, work_model, location_city, location_state, deadline_date, partner_orgs(name, is_auto_created, logo_url), crawler_metadata(company_name, company_logo_url, source_name)")
        .eq("status", "publicada")
        .order("published_at", { ascending: false })
        .limit(3);
      if (error) throw error;
      return data;
    },
  });

  if (isLoading || signalsLoading) {
    return (
      <div className="page-container flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const nome = profile?.full_name?.split(" ")[0] || "Aluno";
  const doneCount = userSignals.filter(s => s.status === "done").length;
  const totalSignals = userSignals.length || signals.length;
  const status = (profile?.profile_status as "construction" | "evolution" | "ready") || "construction";

  const handleAcceptContact = async (requestId: string) => {
    try {
      await respond.mutateAsync({ requestId, decision: "accepted" });
      toast({ title: "Contato aceito!", description: "Seus dados de contato foram liberados para o parceiro." });
    } catch {
      toast({ title: "Erro ao aceitar", variant: "destructive" });
    }
  };

  const handleRejectContact = async (requestId: string) => {
    try {
      await respond.mutateAsync({ requestId, decision: "rejected", reason: rejectReason });
      toast({ title: "Contato recusado" });
      setRejectingId(null);
      setRejectReason("");
    } catch {
      toast({ title: "Erro ao recusar", variant: "destructive" });
    }
  };

  const pendingRequests = contactRequests.filter((r) => r.status === "waiting_student");

  const deadlineLabel = (d: string | null) => {
    if (!d) return null;
    const diff = Math.ceil((new Date(d).getTime() - Date.now()) / 86400000);
    if (diff < 0) return "Encerrada";
    if (diff === 0) return "Último dia";
    return `${diff} dias`;
  };

  // Determine next step from career signals
  const getNextStep = () => {
    const notDone = signals.find(s => {
      const us = userSignals.find(u => u.signal_id === s.id);
      return !us || us.status !== "done";
    });
    return notDone?.name || "Perfil atualizado!";
  };

  return (
    <div className="page-container">
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
        <p className="text-muted-foreground text-sm">Olá,</p>
        <h1 className="font-heading text-2xl font-bold text-foreground">{nome} 👋</h1>
      </motion.div>

      {/* Status Card */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
        className="bg-card rounded-2xl p-4 shadow-card border border-border mb-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-sm text-muted-foreground mb-1">Status do Perfil</p>
            <StatusBadge status={status} />
          </div>
          <div className="text-right">
            <p className="text-sm text-muted-foreground mb-1">Sinais de Carreira</p>
            <SignalProgress current={doneCount} total={totalSignals} />
          </div>
        </div>
        <div className="bg-accent/10 rounded-xl p-3 flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-accent" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-foreground">Próximo passo</p>
            <p className="text-xs text-muted-foreground">{getNextStep()}</p>
          </div>
          <Button size="sm" variant="accent" onClick={() => navigate("/aluno/carreira")}>Fazer</Button>
        </div>
      </motion.div>

      {/* Contact Requests Section */}
      {pendingRequests.length > 0 && (
        <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <h2 className="font-heading text-lg font-semibold text-foreground">Pedidos de Contato</h2>
              <Badge variant="destructive" className="text-xs">{pendingCount}</Badge>
            </div>
          </div>
          <div className="space-y-3">
            {pendingRequests.map((req) => (
              <div key={req.id} className="bg-card rounded-2xl p-4 shadow-card border border-primary/20">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                    <MessageCircle className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-foreground text-sm">{(req.partner_orgs as any)?.name ?? "Empresa parceira"}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">quer entrar em contato com você</p>
                  </div>
                </div>
                {req.message && (
                  <div className="bg-secondary rounded-xl p-3 mb-3">
                    <p className="text-xs text-foreground">{req.message}</p>
                  </div>
                )}
                <div className="bg-muted/50 rounded-xl p-3 mb-3 space-y-1">
                  <p className="text-xs font-medium text-foreground">O que será compartilhado:</p>
                  <p className="text-xs text-muted-foreground">E-mail e telefone/WhatsApp</p>
                  <p className="text-xs font-medium text-foreground mt-2">O que NÃO será compartilhado:</p>
                  <p className="text-xs text-muted-foreground">Renda, endereço, dados privados</p>
                </div>
                {rejectingId === req.id ? (
                  <div className="space-y-2">
                    <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Motivo da recusa (opcional)" className="min-h-[60px] text-sm" />
                    <div className="flex gap-2">
                      <Button size="sm" variant="destructive" className="flex-1" onClick={() => handleRejectContact(req.id)}>Confirmar Recusa</Button>
                      <Button size="sm" variant="ghost" onClick={() => { setRejectingId(null); setRejectReason(""); }}>Cancelar</Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Button size="sm" className="flex-1" onClick={() => handleAcceptContact(req.id)}><Check className="w-4 h-4 mr-1" /> Aceitar</Button>
                    <Button size="sm" variant="outline" className="flex-1" onClick={() => setRejectingId(req.id)}><X className="w-4 h-4 mr-1" /> Recusar</Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </motion.section>
      )}

      {/* Vagas em Destaque — real data */}
      <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-lg font-semibold text-foreground">Vagas em Destaque</h2>
          <Button variant="link" size="sm" onClick={() => navigate("/aluno/vagas")} className="text-primary">
            Ver todas<ArrowRight className="w-4 h-4" />
          </Button>
        </div>
        <div className="space-y-3">
          {(featuredJobs ?? []).length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">Nenhuma vaga publicada no momento.</p>
          )}
          {(featuredJobs ?? []).map((vaga) => {
            const companyName = getCompanyName(vaga);
            const logoUrl = getCompanyLogoUrl(vaga);
            const dl = deadlineLabel(vaga.deadline_date);
            const location = vaga.work_model === "remoto" ? "Remoto" : [vaga.location_city, vaga.location_state].filter(Boolean).join(", ");
            return (
              <div key={vaga.id} onClick={() => navigate(`/aluno/vagas/${vaga.id}`)} className="card-interactive cursor-pointer">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                    {logoUrl ? (
                      <img src={logoUrl} alt="" className="w-7 h-7 rounded object-contain" />
                    ) : (
                      <Briefcase className="w-5 h-5 text-primary" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-foreground truncate">{vaga.title}</p>
                    <p className="text-sm text-muted-foreground">{companyName}</p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                      {location && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{location}</span>}
                      {dl && <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{dl}</span>}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </motion.section>

      {/* Aprender */}
      <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-lg font-semibold text-foreground">Aprender</h2>
          <Button variant="link" size="sm" onClick={() => navigate("/aluno/aprender")} className="text-primary">
            Ver cursos<ArrowRight className="w-4 h-4" />
          </Button>
        </div>
        <div onClick={() => navigate("/aluno/aprender")} className="card-interactive cursor-pointer">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-success/10 flex items-center justify-center shrink-0">
              <BookOpen className="w-5 h-5 text-success" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-foreground">Ciclo Básico</p>
              <p className="text-sm text-muted-foreground">Fundamentos para carreiras digitais</p>
              <p className="text-xs text-muted-foreground mt-1">4 semanas</p>
            </div>
          </div>
        </div>
        <div className="mt-4 p-4 bg-primary/5 rounded-2xl border border-primary/10">
          <p className="text-sm text-foreground font-medium mb-1">🎯 Saia com um case de portfólio</p>
          <p className="text-xs text-muted-foreground">Aprenda fazendo e tenha evidência real para entrevistas.</p>
        </div>
      </motion.section>

      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
        className="text-center text-xs text-muted-foreground mt-8">
        Este perfil representa uma trajetória, não um currículo perfeito.
      </motion.p>
    </div>
  );
}
